// Native Bridge Service for Flutter Communication
class NativeBridgeService {
  constructor() {
    this.isFlutter = false;
    this.init();
  }

  init() {
    console.log('🔧 nativeBridge.init() called');
    console.log('🔧 window.flutterBridge:', !!window.flutterBridge);
    console.log('🔧 window.Flutter:', !!window.Flutter);
    
    // Check if we're running in Flutter WebView
    if (window.flutterBridge) {
      this.isFlutter = true;
      console.log('✅ Native bridge detected - running in Flutter app');
    } else {
      console.log('❌ Native bridge not available - running in web browser');
    }
  }

  // Settings Button Control
  async showSettingsButton() {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    
    if (!isFlutter) {
      console.log('showSettingsButton: Not in Flutter app');
      return false;
    }

    try {
      // Check if the function exists before calling it
      if (typeof window.flutterBridge.showSettingsButton !== 'function') {
        console.warn('showSettingsButton function not available in Flutter bridge');
        return false;
      }
      
      const result = await window.flutterBridge.showSettingsButton();
      console.log('Settings button shown:', result);
      return result;
    } catch (error) {
      console.error('Error showing settings button:', error);
      return false;
    }
  }

  async hideSettingsButton() {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    
    if (!isFlutter) {
      console.log('hideSettingsButton: Not in Flutter app');
      return false;
    }

    try {
      // Check if the function exists before calling it
      if (typeof window.flutterBridge.hideSettingsButton !== 'function') {
        console.warn('hideSettingsButton function not available in Flutter bridge');
        return false;
      }
      
      const result = await window.flutterBridge.hideSettingsButton();
      console.log('Settings button hidden:', result);
      return result;
    } catch (error) {
      console.error('Error hiding settings button:', error);
      return false;
    }
  }

  // Biometric Authentication
  async authenticateWithBiometrics() {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    
    if (!isFlutter) {
      console.log('authenticateWithBiometrics: Not in Flutter app');
      return false;
    }

    try {
      const result = await window.flutterBridge.authenticateWithBiometrics();
      console.log('Biometric authentication result:', result);
      return result;
    } catch (error) {
      console.error('Error with biometric authentication:', error);
      return false;
    }
  }

  async isBiometricAvailable() {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    
    if (!isFlutter) {
      console.log('isBiometricAvailable: Not in Flutter app');
      return false;
    }

    try {
      const result = await window.flutterBridge.isBiometricAvailable();
      console.log('Biometric available:', result);
      return result;
    } catch (error) {
      console.error('Error checking biometric availability:', error);
      return false;
    }
  }

  // Storage
  async setStorageValue(key, value) {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    
    if (!isFlutter) {
      console.log('setStorageValue: Not in Flutter app');
      return false;
    }

    try {
      const result = await window.flutterBridge.setStorageValue(key, value);
      console.log('Storage value set:', result);
      return result;
    } catch (error) {
      console.error('Error setting storage value:', error);
      return false;
    }
  }

  async getStorageValue(key) {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    
    if (!isFlutter) {
      console.log('getStorageValue: Not in Flutter app');
      return null;
    }

    try {
      const result = await window.flutterBridge.getStorageValue(key);
      console.log('Storage value retrieved:', result);
      return result;
    } catch (error) {
      console.error('Error getting storage value:', error);
      return null;
    }
  }

  // Camera
  async capturePhoto() {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    
    if (!isFlutter) {
      console.log('capturePhoto: Not in Flutter app');
      return null;
    }

    try {
      // Use the original working method
      const result = await window.flutterBridge.capturePhoto();
      console.log('Photo captured:', result);
      return result;
    } catch (error) {
      console.error('Error capturing photo:', error);
      return null;
    }
  }

  // QR Scanner
  async scanQRCode() {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    
    if (!isFlutter) {
      console.log('scanQRCode: Not in Flutter app');
      return null;
    }

    try {
      const result = await window.flutterBridge.scanQRCode();
      console.log('QR code scanned:', result);
      return result;
    } catch (error) {
      console.error('Error scanning QR code:', error);
      return null;
    }
  }

  // Location
  async getCurrentLocation() {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    
    if (!isFlutter) {
      console.log('getCurrentLocation: Not in Flutter app');
      return null;
    }

    try {
      const result = await window.flutterBridge.getCurrentLocation();
      console.log('Location retrieved:', result);
      return result;
    } catch (error) {
      console.error('Error getting location:', error);
      return null;
    }
  }

  // Device Info
  async getDeviceInfo() {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    
    if (!isFlutter) {
      console.log('getDeviceInfo: Not in Flutter app');
      return null;
    }

    try {
      const result = await window.flutterBridge.getDeviceInfo();
      console.log('Device info retrieved:', result);
      return result;
    } catch (error) {
      console.error('Error getting device info:', error);
      return null;
    }
  }

  // Check-in/out
  async checkIn(employeeId, remark = null) {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    
    if (!isFlutter) {
      console.log('checkIn: Not in Flutter app');
      return null;
    }

    try {
      const result = await window.flutterBridge.checkIn(employeeId, remark);
      console.log('Check-in result:', result);
      return result;
    } catch (error) {
      console.error('Error during check-in:', error);
      return null;
    }
  }

  async checkOut(employeeId, remark = null) {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    
    if (!isFlutter) {
      console.log('checkOut: Not in Flutter app');
      return null;
    }

    try {
      const result = await window.flutterBridge.checkOut(employeeId, remark);
      console.log('Check-out result:', result);
      return result;
    } catch (error) {
      console.error('Error during check-out:', error);
      return null;
    }
  }

  // Notifications
  async showNotification(title, body, data = {}) {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    
    if (!isFlutter) {
      console.log('showNotification: Not in Flutter app');
      return false;
    }

    try {
      const result = await window.flutterBridge.showNotification(title, body, data);
      console.log('Notification shown:', result);
      return result;
    } catch (error) {
      console.error('Error showing notification:', error);
      return false;
    }
  }

  // Utility methods
  isRunningInFlutter() {
    // Check if Flutter bridge is available
    const hasFlutterBridge = !!(window.flutterBridge && typeof window.flutterBridge.downloadAndSaveFile === 'function')
    const hasFlutter = !!window.Flutter
    
    console.log('🔧 isRunningInFlutter check:')
    console.log('🔧 this.isFlutter:', this.isFlutter)
    console.log('🔧 hasFlutterBridge:', hasFlutterBridge)
    console.log('🔧 hasFlutter:', hasFlutter)
    
    // Update internal state if bridge is detected
    if (hasFlutterBridge || hasFlutter) {
      this.isFlutter = true
      console.log('✅ Flutter bridge detected, updating isFlutter to true')
    }
    
    return this.isFlutter || hasFlutterBridge || hasFlutter
  }

  // Force check Flutter bridge
  checkFlutterBridge() {
    console.log('🔧 Checking Flutter bridge...');
    console.log('🔧 window.flutterBridge:', !!window.flutterBridge);
    console.log('🔧 window.Flutter:', !!window.Flutter);
    console.log('🔧 this.isFlutter:', this.isFlutter);
    
    // Re-check bridge availability
    if (window.flutterBridge) {
      this.isFlutter = true;
      console.log('✅ Flutter bridge detected');
    } else {
      this.isFlutter = false;
      console.log('❌ Flutter bridge not detected');
    }
    
    return this.isFlutter;
  }

  // Helper method to ensure bridge is checked before native calls
  async ensureFlutterBridge() {
    // Check bridge if not already detected
    if (!this.isFlutter) {
      this.checkFlutterBridge();
    }
    
    // Add a small delay to ensure bridge is fully initialized
    if (this.isFlutter && window.flutterBridge) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    return this.isFlutter;
  }

  // Open native settings
  async openNativeSettings() {
    console.log('🔧 nativeBridge.openNativeSettings called');
    
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    console.log('🔧 this.isFlutter:', this.isFlutter);
    console.log('🔧 window.flutterBridge:', !!window.flutterBridge);
    
    if (!isFlutter) {
      console.log('❌ openNativeSettings: Not in Flutter app');
      return false;
    }

    try {
      console.log('🔧 Calling window.flutterBridge.openNativeSettings()');
      const result = await window.flutterBridge.openNativeSettings();
      console.log('✅ Native settings opened:', result);
      return result;
    } catch (error) {
      console.error('❌ Error opening native settings:', error);
      return false;
    }
  }

  // Auto-hide settings button on certain pages
  autoHideSettingsOnPages() {
    const currentPath = window.location.pathname;
    const hideOnPaths = [
      '/login',
      '/welcome',
      '/auth',
      '/register'
    ];

    if (hideOnPaths.some(path => currentPath.includes(path))) {
      this.hideSettingsButton();
    } else {
      this.showSettingsButton();
    }
  }

  async pickGallery() {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    if (!isFlutter) {
      console.log('pickGallery: Not in Flutter app');
      return null;
    }
    try {
      // Set up progress indicator listener
      this.setupProgressListener('camera');
      
      // Use the original working method
      const result = await window.flutterBridge.pickGallery();
      console.log('Gallery photo picked:', result);
      return result;
    } catch (error) {
      console.error('Error picking gallery photo:', error);
      return null;
    }
  }

  async pickFile() {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    if (!isFlutter) {
      console.log('pickFile: Not in Flutter app');
      return null;
    }
    try {
      // Check if the function exists before calling it
      if (typeof window.flutterBridge.pickFile !== 'function') {
        console.warn('pickFile function not available in Flutter bridge');
        return null;
      }
      
      const result = await window.flutterBridge.pickFile();
      console.log('File picked:', result);
      return result;
    } catch (error) {
      console.error('Error picking file:', error);
      return null;
    }
  }

  // iOS-specific document handling methods
  async openDocumentInApp(documentInfo) {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    if (!isFlutter) {
      console.log('openDocumentInApp: Not in Flutter app');
      return false;
    }
    try {
      const result = await window.flutterBridge.openDocumentInApp(documentInfo);
      console.log('Document opened in app:', result);
      return result;
    } catch (error) {
      console.error('Error opening document in app:', error);
      return false;
    }
  }

  async downloadDocument(documentInfo) {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    if (!isFlutter) {
      console.log('downloadDocument: Not in Flutter app');
      return false;
    }
    try {
      const result = await window.flutterBridge.downloadDocument(documentInfo);
      console.log('Document downloaded:', result);
      return result;
    } catch (error) {
      console.error('Error downloading document:', error);
      return false;
    }
  }

  async viewDocument(documentInfo) {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    if (!isFlutter) {
      console.log('viewDocument: Not in Flutter app');
      return false;
    }
    try {
      const result = await window.flutterBridge.viewDocument(documentInfo);
      console.log('Document viewed:', result);
      return result;
    } catch (error) {
      console.error('Error viewing document:', error);
      return false;
    }
  }

  // Native file download and save functionality
  async saveFileToDevice(fileUrl, fileName, fileType = 'document') {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    if (!isFlutter) {
      console.log('saveFileToDevice: Not in Flutter app');
      return false;
    }
    try {
      const result = await window.flutterBridge.saveFileToDevice(fileUrl, fileName, fileType);
      console.log('File saved to device:', result);
      return result;
    } catch (error) {
      console.error('Error saving file to device:', error);
      return false;
    }
  }

  async downloadAndSaveFile(documentInfo) {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    if (!isFlutter) {
      console.log('downloadAndSaveFile: Not in Flutter app');
      return false;
    }
    try {
      console.log('🔧 downloadAndSaveFile called with:', documentInfo)
      console.log('🔧 Calling window.flutterBridge.downloadAndSaveFile')
      
      const result = await window.flutterBridge.downloadAndSaveFile(documentInfo);
      console.log('🔧 downloadAndSaveFile result:', result);
      return result;
    } catch (error) {
      console.error('Error downloading and saving file:', error);
      return false;
    }
  }

  // Open saved file functionality
  async openSavedFile(filePath, fileName, fileType = 'document') {
    // Ensure Flutter bridge is available
    const isFlutter = await this.ensureFlutterBridge();
    if (!isFlutter) {
      console.log('openSavedFile: Not in Flutter app');
      return false;
    }
    try {
      const result = await window.flutterBridge.openSavedFile(filePath, fileName, fileType);
      console.log('File opened:', result);
      return result;
    } catch (error) {
      console.error('Error opening saved file:', error);
      return false;
    }
  }

  // Check if running on iOS
  isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  // Check if running on Android
  isAndroid() {
    return /Android/.test(navigator.userAgent);
  }

  // Get platform-specific document handling method
  getDocumentHandlingMethod() {
    if (this.isIOS()) {
      return 'native'; // Use native iOS document handling
    } else if (this.isAndroid()) {
      return 'native'; // Use native Android document handling
    } else {
      return 'web'; // Use web-based document handling
    }
  }

  setupProgressListener(action) {
    // Remove any existing listeners
    if (window.progressListener) {
      window.removeEventListener('message', window.progressListener);
    }

    // Create new progress listener
    window.progressListener = (event) => {
      if (event.data && event.data.type === 'progress' && event.data.action === action) {
        console.log('Progress update:', event.data.progress);
        
        // Show progress in UI
        this.showProgress(event.data.progress);
      }
    };

    window.addEventListener('message', window.progressListener);
  }

  showProgress(message) {
    // Create or update progress indicator
    let progressElement = document.getElementById('flutter-progress');
    if (!progressElement) {
      progressElement = document.createElement('div');
      progressElement.id = 'flutter-progress';
      progressElement.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 20px;
        border-radius: 10px;
        z-index: 9999;
        font-size: 16px;
        text-align: center;
        min-width: 200px;
      `;
      document.body.appendChild(progressElement);
    }
    
    progressElement.textContent = message;
    
    // Auto-hide for success messages or when operation completes
    if (message.includes('successfully') || message.includes('completed') || message.includes('processed')) {
      setTimeout(() => {
        if (progressElement && progressElement.parentNode) {
          progressElement.parentNode.removeChild(progressElement);
        }
      }, 2000);
    }
    
    // Also hide progress after 10 seconds to prevent hanging
    setTimeout(() => {
      if (progressElement && progressElement.parentNode) {
        progressElement.parentNode.removeChild(progressElement);
      }
    }, 10000);
  }
}

// Create singleton instance
const nativeBridge = new NativeBridgeService();

export default nativeBridge; 